define("tmds/geometry/Extent", [
	"jquery",
	"./Geometry",
	"./Point",
	"./mathUtils",
	"../validator"
], function($, Geometry, Point, mathUtils, validator) {
	"use strict";

	/**
	 * construct a new extent
	 * @param options {Object} option object with below structure
	 * 		{ 
	 * 			xmin: 0,
	 * 			ymin: 0,
	 * 			xmax: 500,
	 * 			ymax: 600 
	 * 		}
	 */
	var Extent = function(options) {
		this.xmax = 0;
		this.xmin = 0;
		this.ymax = 0;
		this.ymin = 0;
		
		if (options != undefined) {			
			if(validator.isDataObject(options)) {				
				['xmax', 'xmin', 'ymax', 'ymin'].forEach(function(prop){					
					if(options[prop] != undefined) {				
						if(! validator.isNumber(options[prop])) {
							throw new Error('Invalid ' + prop + ' value - ' + options[prop] + ' passed');
						}
							
						this[prop] = options[prop];
					}
				}, this);
			}
			else {
			 	throw new Error('Invalid options values - ' + options + ' passed');
		 	}
		}
	};
	
	/**
	 * get the center of the extent
	 * @return {tmds.geometry.Point} the center of the extent
	 */		
	function getCenter() {
		return new Point({
			x: (this.xmin + this.xmax) / 2,
			y: (this.ymin + this.ymax) / 2
		});
	}
		
	/**
	 * get the height of the extent
	 * @return {Number} the height of the extent
	 */
	function getHeight() {
		return Math.abs(this.ymax - this.ymin);
	}
		
	/**
	 * get the width of the extent
	 * @return {Number} the width of the extent
	 */
	function getWidth() {
		return Math.abs(this.xmax - this.xmin);
	}
	
	/**
	 * check if the input geometry intersects with the extent
	 * @param geom {tmds.geometry.Geometry}
	 * @return {Boolean}
	 */
	function intersects(geom) {
		if (!geom) {
			return false;
		}
			
		switch(geom.type) {
			case "Point":
				return contains.call(this, geom);
			case "Line":
				return _intersectsLine.call(this, geom);
			case "Polyline":
				return _intersectsPolyline.call(this, geom);
			case "Extent":
				return !!_intersectsExtent.call(this, geom);
			default:
				return false;
		}
	}
	
	/**
	 * check if the extent contains the input geometry
	 * @param geom {tmds.geometry.Geometry} Point | Line | Polyline | Extent
	 * @return {Boolean}
	 */
	function contains(geom) {
		if (!geom) {
			return false;
		}
		
		switch (geom.type) {
			case "Point": 
				return geom.x >= this.xmin && geom.x <= this.xmax && geom.y >= this.ymin && geom.y <= this.ymax;
			case "Line":
			case "Polyline":
				return _containsExtent.call(this, geom.toExtent());				
			case "Extent":
				return _containsExtent.call(this, geom);
		}
		
		return false;
	}
	
	/**
	 * check if this extent contains the input extent
	 * @param ext {tmds.geometry.Extent}
	 * @return {Boolean}
	 */
	function _containsExtent(ext) {
		return ext.xmin >= this.xmin && ext.xmax <= this.xmax && ext.ymin >= this.ymin && ext.ymax <= this.ymax;
	}
	
	/**
	 * check if this extent intersects with the input extent
	 * @param ext {tmds.geometry.Extent}
	 * @return {tmds.geometry.Extent} | null
	 */
	function _intersectsExtent(ext) {
		var x, y, dx, dy;
		
		if (ext instanceof Extent) {
			x = Math.max(this.xmin, ext.xmin);
			y = Math.max(this.ymin, ext.ymin);
			dx = Math.min(this.xmax, ext.xmax) - x;
			dy = Math.min(this.ymax, ext.ymax) - y;
		}
		
		return (dx < 0 || dy < 0) ? null : new Extent({xmin: x, ymin: y, xmax: x + dx, ymax: y + dy});
	}
	
	/**
	 * check if this extent intersects with the input line
	 * @param line {tmds.geometry.Line}
	 * @return {Boolean}
	 */
	function _intersectsLine(line) {
		if (line.type === "Line") {
			var pnt1 = new Point({x: line.x1, y: line.y1}),
				pnt2 = new Point({x: line.x2, y: line.y2});
		
			// covers the following scenarios:
			//	- line is completely within the extent
			//	- line end point touches the extent boundary
			var insideOrTouch = [pnt1, pnt2].some(function(pnt) {
				return contains.call(this, pnt);
			}, this);

			// covers if the line crosses the extent and both line end points are outside the extent.
			var across = [ {x1: this.xmin, y1: this.ymin, x2: this.xmax, y2: this.ymin},
			               {x1: this.xmax, y1: this.ymin, x2: this.xmax, y2: this.ymax},
			               {x1: this.xmax, y1: this.ymax, x2: this.xmin, y2: this.ymax},
			               {x1: this.xmin, y1: this.ymax, x2: this.xmin, y2: this.ymin}].some(function(aLine) {
			            	   return mathUtils.lineIntersectsLine(aLine, line);
			               });
			
			return insideOrTouch || across;
		}
		else {
			return false;
		}
	}
	
	/**
	 * check if this extent intersects with the input polyline
	 * @param polyline {tmds.geometry.Polyline}
	 * @return {Boolean}
	 */
	function _intersectsPolyline(polyline) {
		if (polyline.type === "Polyline") {
			return polyline.paths.some(function(aPath) {
				var i = 0, line;
				while (i < aPath.length - 1) {
					line = {
						x1: aPath[i][0],
						y1: aPath[i][1],
						x2: aPath[i+1][0],
						y2: aPath[i+1][1]
					};
					
					if (mathUtils.lineIntersectsLine(this, line)) {
						return true;
					}
					
					i++;
				}
				
				return false;
				
			}, this);
		}
		else {
			return false;
		}
	}
	
	/**
	 * union this extent with another, and return a new extent
	 * @return {tmds.geometry.Extent}
	 */
	function union(ext) {
		return new Extent({
			xmin: Math.min(this.xmin, ext.xmin),
			ymin: Math.min(this.ymin, ext.ymin),
			xmax: Math.max(this.xmax, ext.xmax),
			ymax: Math.max(this.ymax, ext.ymax)
		});
	}
	
	/**
	 * return the extent in JSON object format
	 * @return {Object} extent representation in JSON object
	 */
	function toJson() {
		return {
			xmax: this.xmax,
			xmin: this.xmin,
			ymax: this.ymax,
			ymin: this.ymin,
			type: this.type
		};
	}
	
	Extent.prototype = $.extend(Object.create(Geometry.prototype), {		
		declaredClass: "tmds.geometry.Extent",
		type: "Extent",
		'getCenter' : getCenter,
		'getHeight' : getHeight,
		'getWidth' : getWidth,
		'intersects' : intersects,
		'contains' : contains,
		'union' : union,
		'toJson' : toJson
	});
		
	return Extent;
});